<template>
  <div class="pa-setup-host">
    <!-- Exception overlay from the status engine (e.g. setup invoice overdue): above the title,
         as in the design project view (pa-app.jsx, inSetup) -->
    <div v-if="exception" class="pa-setup-host__exception">
      <div class="pa-trackcard" role="alert">
        <i class="material-icons" aria-hidden="true">warning</i>
        <p><b>{{ exception.title }}.</b> {{ exception.body }}</p>
        <a
          v-if="exception.url"
          class="pa-btn pa-btn--orange pa-btn--pill"
          :href="exception.url"
          target="_blank"
          rel="noopener noreferrer"
        >{{ exception.cta || 'Pay invoice now' }}</a>
      </div>
    </div>

    <h1 class="pa-setup-host__title">Set up your POP Authority project</h1>
    <p class="pa-setup-host__subtitle">
      Your setup fee includes the full audit. We analyze your entire site and create content plans to grow your traffic.
    </p>

    <div v-if="!(site && site.id)" class="pa-setup-host__loading">
      Loading site…
    </div>
    <setup-wizard
      v-else
      :key="site.id"
      :site="site"
      :status="status"
      @audits-changed="$emit('audits-changed', $event)"
    />
  </div>
</template>

<script>
import SetupWizard from '@/components/SitesSingleView/PopAuthority/setup/SetupWizard.vue'

/**
 * Inline setup wizard shown by the POP Authority layout while a site is in its setup phase
 * (design: pa-setup.jsx SetupWizard inside pa-app.jsx `.pa-wrap.pa-narrow`). Renders the
 * exception card, the title and the subtitle; the steps live in SetupWizard.
 */
export default {
  name: 'PopAuthoritySetupWizardHost',
  components: { SetupWizard },
  props: {
    site: { type: Object, default: null },
    /** Per-site status row (siteProjectStatuses): exception card + setup invoice for the wizard. */
    status: { type: Object, default: null },
  },
  computed: {
    exception () {
      return (this.status && this.status.exception) || null
    },
  },
}
</script>

<style lang="scss">
/* Not scoped on purpose. The app's global `* { font-size: .85rem; line-height: 18px }` rule
   (assets/main.scss) applies to EVERY element, so a container's font size never reaches the text
   inside it (a <span> in a .92rem invoice row still rendered at .85rem). Inside the setup host
   every element inherits again, like the design's `.pa-app` base. Specificity is exactly (0,1,0):
   it beats the global `*`, loses to every scoped component rule (`.cls[data-v-…]`, (0,2,0)) and
   to the Vuetify / Bulma / Buefy class rules, which load after this style and win ties. The icon
   fonts (loaded from index.html, before this style) are excluded so they keep their own sizes. */
.pa-setup-host :where(*:not(.material-icons):not(.material-icons-outlined):not(.material-icons-round):not(.mdi):not(.fa)) {
  font-size: inherit;
  line-height: inherit;
}
</style>

<style lang="scss" scoped>
@import './setup/setup.scss';

/* Design `.pa-wrap.pa-narrow` (centred, 960px) + the `.pa-app` text base (0.9rem / 1.45, teal ink),
   which the inheritance restore above passes down to the wizard. */
.pa-setup-host {
  max-width: 960px;
  margin: 0 auto;
  font-size: 0.9rem;
  line-height: 1.45;
  color: $pa-teal;

  &__exception {
    margin-bottom: 14px;

    .pa-trackcard {
      margin: 0;
      max-width: none;

      p b {
        display: inline;
      }
    }
  }

  /* pa.css .pa-h1 */
  &__title {
    font-size: 1.9rem;
    font-weight: 800;
    line-height: 1.45;
    margin: 0 0 6px;
    color: $pa-teal;
  }

  /* pa.css .pa-sub, colors_and_type.css --pop-ink-muted */
  &__subtitle {
    font-size: 0.9rem;
    line-height: 1.45;
    color: #6b7b7e;
    margin: 0 0 20px;
  }

  &__loading {
    font-size: 0.9rem;
    line-height: 1.45;
    margin: 16px 0;
  }
}
</style>
